import React, { useState, useEffect } from "react";
import { getAppResources, API_URL } from "../../services/api.js";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";
import toast from "react-hot-toast";

const ConfluenceResourceModal = ({ appName, userGoogleId, onClose }) => {
    const [resources, setResources] = useState([]);
    const [selectedResource, setSelectedResource] = useState(null);
    const [selectedSpaceKeys, setSelectedSpaceKeys] = useState(new Set());
    const [expandedResourceId, setExpandedResourceId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmLoading, setConfirmLoading] = useState(false);

    useEffect(() => {
        const fetchResources = async () => {
            try {
                setLoading(true);
                const response = await getAppResources(appName, userGoogleId);
                setResources(response.resources || []);
                
                if (response.selected_resource) {
                    const resource = response.selected_resource;
                    setSelectedResource(resource);
                    setExpandedResourceId(resource.id);
                    // Use selected_spaces for Confluence
                    const initialKeys = new Set(resource.selected_spaces?.map(s => s.key) || []);
                    setSelectedSpaceKeys(initialKeys);
                }
            } catch (error) {
                toast.error("Failed to fetch Confluence resources.");
            } finally {
                setLoading(false);
            }
        };
        fetchResources();
    }, [appName, userGoogleId]);

    const handleToggleSpace = (spaceKey) => {
        setSelectedSpaceKeys(prevKeys => {
            const newKeys = new Set(prevKeys);
            newKeys.has(spaceKey) ? newKeys.delete(spaceKey) : newKeys.add(spaceKey);
            return newKeys;
        });
    };
    
    const handleSetResource = (resource) => {
        if (selectedResource?.id !== resource.id) {
            setSelectedResource(resource);
            setSelectedSpaceKeys(new Set()); 
            setExpandedResourceId(resource.id); 
        }
    };

    const handleToggleExpand = (resourceId) => {
        setExpandedResourceId(prevId => prevId === resourceId ? null : resourceId);
    };

    const handleSelectAll = () => {
        if (!selectedResource) return;
        
        const allSpaceKeys = selectedResource.spaces.map(s => s.key);

        if (selectedSpaceKeys.size === allSpaceKeys.length) {
            setSelectedSpaceKeys(new Set());
        } else {
            setSelectedSpaceKeys(new Set(allSpaceKeys));
        }
    };

    const handleConfirmSelection = async () => {
        if (!selectedResource) {
            toast.error("Please select a resource before confirming.");
            return;
        }
        setConfirmLoading(true);
        try {
            const body = {
                selected_resource_id: selectedResource.id,
                space_keys: Array.from(selectedSpaceKeys), // Send space_keys
            };
            const response = await fetch(`${API_URL}/auth/confluence/resources/select`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${userGoogleId}` },
                body: JSON.stringify(body),
                credentials: "include"
            });
            if (!response.ok) throw new Error("Failed to update selection.");
            toast.success("Confluence configuration saved!");
            onClose();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setConfirmLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-2 border border-zinc-700 w-full max-w-xl max-h-[90%] rounded-lg p-6 flex flex-col">
                <div className="flex justify-between items-center mb-4">
                   <h2 className="text-lg font-semibold text-zinc-200">Confluence Configuration</h2>
                   <button onClick={onClose} className="text-zinc-500 hover:text-white"><IoMdClose size={24} /></button>
                </div>

                <div className="flex-grow overflow-auto space-y-3">
                    {loading ? <p>Loading...</p> : resources.map(resource => {
                        const isSelected = selectedResource?.id === resource.id;
                        const isExpanded = expandedResourceId === resource.id;
                        return (
                            <div key={resource.id} className={`border rounded-lg ${isSelected ? "border-[#5652CF]" : "border-zinc-700"}`}>
                                <div 
                                    className={`flex justify-between items-center p-3 cursor-pointer ${isSelected ? "bg-zinc-800" : "bg-zinc-900"}`}
                                    onClick={() => handleToggleExpand(resource.id)}
                                >
                                    <div className="flex items-center gap-3 flex-grow" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="radio"
                                            id={`resource-${resource.id}`}
                                            name="resource-selection"
                                            checked={isSelected}
                                            onChange={() => handleSetResource(resource)}
                                            className="form-radio h-4 w-4 bg-zinc-700 border-zinc-600 text-[#5652CF] focus:ring-[#5652CF]"
                                        />
                                        <img src={resource.avatarUrl} alt={resource.name} className="h-8 w-8 rounded-full" />
                                        <div>
                                            <span className="font-medium text-zinc-200">{resource.name}</span>
                                            <a href={resource.domainUrl} target="_blank" rel="noopener noreferrer" className="block text-xs text-zinc-400 hover:text-blue-400 hover:underline truncate max-w-xs">
                                                {resource.domainUrl}
                                            </a>
                                        </div>
                                    </div>
                                    <IoIosArrowDown className={`transition-transform text-zinc-400 ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>

                                {isExpanded && (
                                    <div className="p-4 border-t border-zinc-700 bg-zinc-800">
                                        <div className="flex justify-between items-center mb-3">
                                            <label className={`flex items-center gap-3 text-sm text-zinc-300 ${!isSelected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                                <input 
                                                    type="checkbox"
                                                    onChange={handleSelectAll}
                                                    disabled={!isSelected}
                                                    checked={isSelected && selectedResource.spaces.length > 0 && selectedSpaceKeys.size === selectedResource.spaces.length}
                                                    className="form-checkbox h-4 w-4 bg-zinc-700 border-zinc-600 rounded text-[#5652CF] focus:ring-[#5652CF] disabled:cursor-not-allowed" 
                                                />
                                                Select All
                                            </label>
                                            {isSelected && <span className="text-xs text-zinc-400">{selectedSpaceKeys.size} / {selectedResource.spaces.length} selected</span>}
                                        </div>
                                        
                                        {!isSelected && (
                                            <div className="text-xs text-yellow-400 p-2 bg-yellow-900/50 rounded-md mb-3">
                                                Select this resource to enable space selection.
                                            </div>
                                        )}
                                        <ul className={`space-y-2 max-h-60 overflow-y-auto ${!isSelected ? 'opacity-50' : ''}`}>
                                            {resource.spaces.map(space => (
                                                <li key={space.key} className="flex items-center gap-3 p-2 rounded-md hover:bg-zinc-700">
                                                    <input
                                                        type="checkbox"
                                                        id={space.key}
                                                        checked={selectedSpaceKeys.has(space.key)}
                                                        onChange={() => handleToggleSpace(space.key)}
                                                        disabled={!isSelected}
                                                        className="form-checkbox h-5 w-5 bg-zinc-900 border-zinc-600 text-[#5652CF] focus:ring-[#5652CF] disabled:cursor-not-allowed"
                                                    />
                                                    <label htmlFor={space.key} className={`text-zinc-300 ${!isSelected ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                        {space.name} ({space.key})
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                
                <div className="mt-6 flex justify-end items-center pt-4 border-t border-zinc-700">
                    <button
                        onClick={handleConfirmSelection}
                        disabled={!selectedResource || confirmLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 transition-colors"
                    >
                        {confirmLoading ? "Saving..." : "Save Configuration"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfluenceResourceModal;